import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Check, ChevronRight, User, Users, MessageCircle, Zap, UserPlus, X, Sparkles, SkipForward
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SETUP_STEPS = [
  { 
    id: 'account_created', 
    label: 'Account created',
    icon: User,
    completed: true, // Always completed
    action: null,
    skippable: false
  },
  { 
    id: 'business_selected', 
    label: 'Business type selected',
    icon: Sparkles,
    checkField: 'business_type',
    action: null,
    skippable: false
  },
  { 
    id: 'first_lead', 
    label: 'Add your first lead',
    icon: Users,
    checkField: 'has_leads',
    action: '/leads?action=add',
    buttonText: 'Add Lead',
    skippable: false
  },
  { 
    id: 'whatsapp_connected', 
    label: 'Connect WhatsApp',
    icon: MessageCircle,
    checkField: 'whatsapp_configured',
    action: '/whatsapp',
    buttonText: 'Setup WhatsApp',
    skippable: true
  },
  { 
    id: 'automation_enabled', 
    label: 'Enable automation',
    icon: Zap,
    checkField: 'automation_enabled',
    action: '/automations',
    buttonText: 'Turn On',
    skippable: false
  },
  { 
    id: 'team_invited', 
    label: 'Invite team member',
    icon: UserPlus,
    checkField: 'has_team',
    action: '/team',
    buttonText: 'Invite',
    skippable: true
  },
];

const SetupChecklist = ({ onDismiss }) => {
  const [steps, setSteps] = useState(SETUP_STEPS);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [skippedSteps, setSkippedSteps] = useState([]);
  const navigate = useNavigate();

  // Load dismissed and skipped state from localStorage on mount
  useEffect(() => {
    const savedDismissed = localStorage.getItem('checklist_dismissed');
    const savedSkipped = localStorage.getItem('checklist_skipped');
    if (savedDismissed === 'true') {
      setDismissed(true);
    }
    if (savedSkipped) {
      try {
        setSkippedSteps(JSON.parse(savedSkipped));
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    checkProgress();
  }, [skippedSteps]);

  const handleDismissAll = () => {
    setDismissed(true);
    localStorage.setItem('checklist_dismissed', 'true');
    if (onDismiss) onDismiss();
  };

  const handleSkipStep = (stepId) => {
    const newSkipped = [...skippedSteps, stepId];
    setSkippedSteps(newSkipped);
    localStorage.setItem('checklist_skipped', JSON.stringify(newSkipped));
  };

  const checkProgress = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      // Check workspace settings for onboarding
      const settingsRes = await fetch(`${API_URL}/api/auth/workspace-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Check if user has leads
      const leadsRes = await fetch(`${API_URL}/api/leads?limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Check automation settings
      const autoRes = await fetch(`${API_URL}/api/automation/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Check team members
      const teamRes = await fetch(`${API_URL}/api/team/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const settings = settingsRes.ok ? await settingsRes.json() : {};
      const leads = leadsRes.ok ? await leadsRes.json() : { leads: [] };
      const autoSettings = autoRes.ok ? await autoRes.json() : { enabled: false };
      const team = teamRes.ok ? await teamRes.json() : { members: [] };
      
      // Update step completion
      const updatedSteps = SETUP_STEPS.map(step => {
        let completed = step.completed;
        let skipped = skippedSteps.includes(step.id);
        
        switch (step.id) {
          case 'business_selected':
            completed = !!settings.business_type;
            break;
          case 'first_lead':
            completed = (leads.leads?.length || leads.data?.length) > 0;
            break;
          case 'whatsapp_connected':
            completed = !!settings.whatsapp_configured || skipped;
            break;
          case 'automation_enabled':
            completed = autoSettings.enabled;
            break;
          case 'team_invited':
            completed = (team.members?.length || 0) > 1 || skipped;
            break;
          default:
            break;
        }
        
        return { ...step, completed, skipped };
      });
      
      setSteps(updatedSteps);
      
      // Check if all completed (including skipped)
      const allDone = updatedSteps.every(s => s.completed || s.skipped);
      if (allDone) {
        setShowConfetti(true);
        setTimeout(() => {
          setShowConfetti(false);
          handleDismissAll();
        }, 5000);
      }
      
    } catch (error) {
      console.error('Failed to check setup progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const completedCount = steps.filter(s => s.completed || s.skipped).length;
  const totalSteps = steps.length;
  const progressPercent = (completedCount / totalSteps) * 100;
  const allComplete = completedCount === totalSteps;

  if (dismissed || allComplete) {
    return null;
  }

  if (loading) {
    return null;
  }

  return (
    <>
      {showConfetti && <Confetti recycle={false} numberOfPieces={300} />}
      
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-emerald-900 via-emerald-800 to-teal-900 rounded-xl p-6 mb-6 relative overflow-hidden"
        data-testid="setup-checklist"
      >
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="relative">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                Complete Your Setup
              </h3>
              <p className="text-emerald-200/70 text-sm mt-1">
                {completedCount}/{totalSteps} steps done
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDismissAll}
                className="text-emerald-300/70 hover:text-white text-sm underline"
                data-testid="dismiss-checklist-btn"
              >
                Dismiss All
              </button>
              <button
                onClick={handleDismissAll}
                className="text-emerald-300/50 hover:text-white"
                data-testid="close-checklist-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="w-full h-2 bg-emerald-950 rounded-full mb-6 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full"
            />
          </div>
          
          {/* Steps */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`p-3 rounded-lg border transition-all ${
                  step.completed || step.skipped
                    ? 'bg-emerald-500/20 border-emerald-500/30'
                    : 'bg-emerald-900/30 border-emerald-700/30 hover:border-emerald-600/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    step.completed ? 'bg-emerald-500' : step.skipped ? 'bg-gray-500' : 'bg-emerald-800'
                  }`}>
                    {step.completed ? (
                      <Check className="w-4 h-4 text-white" />
                    ) : step.skipped ? (
                      <SkipForward className="w-3 h-3 text-white" />
                    ) : (
                      <step.icon className="w-3 h-3 text-emerald-300" />
                    )}
                  </div>
                  <span className={`text-xs font-medium ${
                    step.completed ? 'text-emerald-300' : step.skipped ? 'text-gray-400' : 'text-white'
                  }`}>
                    {step.completed ? '✓' : step.skipped ? 'Skipped' : index + 1}
                  </span>
                </div>
                
                <p className={`text-sm ${
                  step.completed ? 'text-emerald-300 line-through' : step.skipped ? 'text-gray-400 line-through' : 'text-white'
                }`}>
                  {step.label}
                </p>
                
                {!step.completed && !step.skipped && (
                  <div className="flex items-center gap-2 mt-2">
                    {step.action && (
                      <button
                        onClick={() => navigate(step.action)}
                        className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                        data-testid={`checklist-action-${step.id}`}
                      >
                        {step.buttonText}
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    )}
                    {step.skippable && (
                      <button
                        onClick={() => handleSkipStep(step.id)}
                        className="text-xs text-gray-400 hover:text-gray-300 flex items-center gap-1"
                        data-testid={`checklist-skip-${step.id}`}
                      >
                        Skip
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default SetupChecklist;
